import { useState, useMemo, useCallback, useRef } from 'react';
import { DataTableColumn, DataTableSorting, TableQuery, TableQueryEvent } from '@/components/datatable/tableTypes';
import { sortData } from '@core/utils/tableUtils';

interface UseTableSortOptions<T> {
    columns: DataTableColumn<T>[];
    data: T[];
    sorting?: DataTableSorting;
    customSort?: (data: T[], sortBy: string, sortDir: 'asc' | 'desc') => T[];
    /** Current page — needed to build the full TableQuery on sort change. */
    page: number;
    /** Current page size — needed to build the full TableQuery on sort change. */
    pageSize: number;
    /**
     * Unified query change callback (from WcnTableProps.onQueryChange).
     * When present, sort changes emit a full TableQuery to this callback
     * instead of (or in addition to) sorting.onSortChange.
     */
    onQueryChange?: (query: TableQuery, event: TableQueryEvent) => void;
}

export interface UseTableSortResult<T> {
    /**
     * Data after client-side sort. Same reference as `data` in server-side mode.
     */
    sortedData: T[];
    /**
     * Last non-empty sortedData. Render this when loading=true and sortedData is
     * empty to prevent blank-table flash while new data is in flight.
     */
    staleData: T[];
    activeSortBy: string;
    activeSortDirection: 'asc' | 'desc';
    /**
     * Called by WcnTableHead when the user clicks a sort icon.
     * Handles both client-side state update and server-side callback emission.
     */
    handleSortClick: (columnValue: string, direction: 'asc' | 'desc') => void;
}

/**
 * Manages table sort state for both client-side and server-side modes.
 *
 * ── Mode detection ───────────────────────────────────────────────────────────
 * Server-side: `sorting.onSortChange` present OR `onQueryChange` present
 * Client-side: neither present
 *
 * ── Per-column sort customisation (client-side only) ─────────────────────────
 * Priority: column.comparator → column.valueGetter → customSort prop → sortData utility
 *
 * ── Server-side callback priority ────────────────────────────────────────────
 * `onQueryChange` (full query context) takes priority over `sorting.onSortChange`
 * (sort-only) when both are provided. Use onQueryChange when you need
 * sort + page + pageSize together in one re-fetch call.
 */
export function useTableSort<T>({
    columns,
    data,
    sorting,
    customSort,
    page,
    pageSize,
    onQueryChange
}: UseTableSortOptions<T>): UseTableSortResult<T> {
    // ── Mode detection ────────────────────────────────────────────────────────
    const isServerSide = typeof onQueryChange === 'function' || typeof sorting?.onSortChange === 'function';

    // ── Internal sort state (client-side only) ────────────────────────────────
    const [internalSortBy, setInternalSortBy] = useState<string>(!isServerSide ? (sorting?.defaultSortBy ?? '') : '');
    const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>(
        !isServerSide ? (sorting?.defaultSortDirection ?? 'asc') : 'asc'
    );

    // ── Active sort values ────────────────────────────────────────────────────
    // Server: driven by controlled props (reflect API state)
    // Client: driven by internal state
    const activeSortBy = isServerSide ? (sorting?.sortBy ?? '') : internalSortBy;
    const activeSortDirection = isServerSide ? (sorting?.sortDirection ?? 'asc') : internalSortDirection;

    // ── Sort click handler ────────────────────────────────────────────────────
    // useCallback — WcnTableHead is React.memo; stable ref prevents wasted re-renders
    const handleSortClick = useCallback(
        (columnValue: string, direction: 'asc' | 'desc') => {
            const isSameFieldAndDirection = activeSortBy === columnValue && activeSortDirection === direction;

            // Resolve next sort state
            const nextSortBy = isSameFieldAndDirection ? (sorting?.defaultSortBy ?? '') : columnValue;
            const nextSortDir: 'asc' | 'desc' = isSameFieldAndDirection
                ? (sorting?.defaultSortDirection ?? 'asc')
                : direction;

            if (isServerSide) {
                if (onQueryChange) {
                    // Preferred path: emit full query context — page resets to 0 on sort
                    // (page component can override this in its handler)
                    onQueryChange(
                        { sortBy: nextSortBy, sortDirection: nextSortDir, page: 0, pageSize },
                        TableQueryEvent.SORT
                    );
                } else {
                    // Fallback: sort-only callback
                    sorting?.onSortChange?.(nextSortBy, nextSortDir);
                }
            } else {
                setInternalSortBy(nextSortBy);
                setInternalSortDirection(nextSortDir);
            }
        },
        [isServerSide, activeSortBy, activeSortDirection, sorting, onQueryChange, page, pageSize]
    );

    // ── Client-side definition sort ──────────────────────────────────────────────────────
    // Priority: column.comparator → column.valueGetter → customSort → sortData utility
    const sortedData = useMemo<T[]>(() => {
        if (isServerSide) return data; // server already sorted

        if (!columns.length || !activeSortBy) return data;

        const hasSortableColumns = columns.some((col) => col.sortable !== false);
        if (!hasSortableColumns) return data;

        const activeColumn = columns.find((col) => col.value === activeSortBy);
        const multiplier = activeSortDirection === 'asc' ? 1 : -1;

        // 1. Per-column full comparator (direction applied here)
        if (activeColumn?.comparator) {
            return [...data].sort((a, b) => activeColumn.comparator!(a, b) * multiplier);
        }

        // 2. Per-column value getter → default string/number comparison
        if (activeColumn?.valueGetter) {
            return [...data].sort((a, b) => {
                const aVal = activeColumn.valueGetter!(a);
                const bVal = activeColumn.valueGetter!(b);
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return aVal.localeCompare(bVal) * multiplier;
                }
                return (aVal > bVal ? 1 : -1) * multiplier;
            });
        }

        // 3. Global custom sort (receives full data + field + direction)
        if (customSort) return customSort(data, activeSortBy, activeSortDirection);

        // 4. Default utility — dot-path aware string/number comparison
        return sortData(data, activeSortBy, activeSortDirection);
    }, [data, activeSortBy, activeSortDirection, columns, customSort, isServerSide]);

    // ── Stale data cache ──────────────────────────────────────────────────────
    // Keeps the last non-empty result in a ref (no re-render cost).
    // Used by NewWcnTable to show previous rows while new data is loading,
    // preventing the blank-table flash on sort/page changes.
    const staleDataRef = useRef<T[]>(sortedData);
    if (sortedData.length > 0) {
        staleDataRef.current = sortedData;
    }

    return {
        sortedData,
        staleData: staleDataRef.current,
        activeSortBy,
        activeSortDirection,
        handleSortClick
    };
}
